/**
 * B379 (c) — the visit funnel's two doors (the amendment the item names:
 * "storefront/embed already know carts and orders — visits/views need a
 * lightweight event").
 *
 * `funnel.mark` is the buyer's own coarse journey mark: visit / view /
 * checkout. PAID is deliberately NOT in the vocabulary — the platform
 * holds the orders, so the paid stage is derived from reads the house
 * already trusts, never from a client's claim. The caller's identity is
 * the AUTHENTICATED principal's own id (no customerId travels in the
 * body), consumed only as a per-day dedup key; no PII, no IP, nothing
 * typed rides a mark. Permission is `order:write` — the scope the same
 * buyer already holds for the cart — so no authorization fence moves for
 * this telemetry (additive; reversal is deleting both ops + the store).
 *
 * `funnel.read` answers unique-identity counts per stage to the same
 * books seat report.run serves (ledger:read, MANAGER/ADMIN). Windows are
 * epoch-ms, resolved to inclusive UTC days; unanswerable windows are
 * refused by name.
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId } from "./common.js";

/** The closed stage vocabulary — paid is derived from orders, never claimed. */
export const zFunnelStage = z.enum(["visit", "view", "checkout"]);

export const funnelMark = writeOp({
  name: "funnel.mark",
  permission: "order:write",
  // The storefront/embed buyer only. Machine sellers (RESELLER) and counter
  // devices are deliberately absent: a kiosk's attract loop is not a visit,
  // and widening later is an additive edit.
  principals: ["CUSTOMER"],
  fields: { channelId: zId, stage: zFunnelStage },
  response: z
    .object({
      /** True when this identity was counted for the first time today — a reload answers false. */
      counted: z.boolean(),
    })
    .strict(),
  exampleRequest: { channelId: "web", stage: "visit" },
  exampleResponse: { counted: true },
});

export const funnelRead = readOp({
  name: "funnel.read",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { channelId: zId.optional(), from: zEpochMs, to: zEpochMs },
  response: z
    .object({
      visits: z.number().int().min(0),
      views: z.number().int().min(0),
      checkouts: z.number().int().min(0),
    })
    .strict(),
  exampleRequest: { from: 1_780_000_000_000, to: 1_780_086_400_000 },
  exampleResponse: { visits: 128, views: 64, checkouts: 12 },
});
